import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  ArrowLeftRight,
  Camera,
  Check,
  CircleHelp,
  ExternalLink,
  History,
  LayoutGrid,
  List,
  ListChecks,
  MessageCircle,
  Newspaper,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type {
  MatchParticipantDto,
  MatchResponseStatus,
  MatchSyncHistoryEntry,
  MeResponse,
  RespondablePlayerSearchResult,
  SeasonMatchCommentsResponse,
  SeasonMatchResponse,
  SeasonResponse,
  TurnierDeStatusDto,
  VotingOverview,
} from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button } from '../components/ui';
import { RosterModal, type RosterGroup } from '../components/RosterModal';
import { DateBadge } from '../components/DateBadge';
import { rosterChipClass } from '../lib/roster-chip';
import { responseButtonStyle } from '../lib/response-button-style';
import { useTheme } from '../lib/theme';
import { fileToBase64 } from '../lib/file-upload';
import { useClickOutside } from '../lib/use-click-outside';
import { useLockBodyScroll } from '../lib/use-lock-body-scroll';
import { LineupModal } from '../components/lineup/LineupModal';
import { LineupStatusBadge } from '../components/lineup/LineupStatusBadge';
import { RescheduleModal } from '../components/season/RescheduleModal';

/** Multi-select team filter for the Saisonspiele list — same
 * checkbox-in-popup pattern as TrainingPage's GroupFilterDropdown. */
function TeamFilterDropdown({
  teams,
  selected,
  onChange,
}: {
  teams: Array<{ teamId: string; teamName: string }>;
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }
  const label =
    selected.length === 0
      ? 'Alle Mannschaften'
      : selected.length === 1
        ? (teams.find((t) => t.teamId === selected[0])?.teamName ?? '1 Mannschaft')
        : `${selected.length} Mannschaften`;

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside(rootRef, () => setOpen(false), open);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="cursor-pointer whitespace-nowrap rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700"
      >
        {label} ▾
      </button>
      {open && (
        <div className="absolute left-0 z-10 mt-1 w-56 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
          {teams.length === 0 && <p className="px-2 py-1 text-sm text-slate-400">Keine Mannschaften</p>}
          {teams.map((t) => (
            <label key={t.teamId} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
              <input type="checkbox" checked={selected.includes(t.teamId)} onChange={() => toggle(t.teamId)} />
              {t.teamName}
            </label>
          ))}
          {selected.length > 0 && (
            <button
              type="button"
              className="mt-1 w-full rounded px-2 py-1 text-left text-xs text-blue-600 hover:bg-slate-50"
              onClick={() => onChange([])}
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_LABEL: Record<MatchResponseStatus, string> = {
  accepted: 'zugesagt',
  declined: 'abgesagt',
  maybe: 'vielleicht',
  pending: 'offen',
};

const CHIP_VARIANT: Record<MatchResponseStatus, 'positive' | 'negative' | 'uncertain'> = {
  accepted: 'positive',
  declined: 'negative',
  maybe: 'uncertain',
  pending: 'positive',
};

/** Kapsel-Reihenfolge für die Spieler-Chips — erst männlich (+ kein
 * Geschlecht gesetzt, dieselbe blaue Einfärbung), dann weiblich, dann —
 * unabhängig vom Geschlecht — alle Absagen ganz hinten. Stabiler Sort, damit
 * die Reihenfolge innerhalb einer Gruppe sonst unverändert bleibt. Von
 * `MatchCard` (Karte) **und** `PlayerChipsCell` (Listenansicht) genutzt —
 * eine Regel darf nicht zweimal implementiert werden. */
function sortParticipantsForDisplay(participants: MatchParticipantDto[]): MatchParticipantDto[] {
  return [...participants].sort((a, b) => {
    const rank = (p: MatchParticipantDto) => (p.status === 'declined' ? 2 : p.gender === 'w' ? 1 : 0);
    return rank(a) - rank(b);
  });
}

/** Karten- vs. Listenansicht (§ Nutzeranfrage) — eine reine
 * Browser-Bequemlichkeit wie `MembersPage.tsx`s Spaltenauswahl, deshalb
 * `localStorage` statt einer serverseitigen Präferenz wie beim
 * Saisonspiel-Filter oben: welche Darstellung jemand bevorzugt, muss nicht
 * geräteübergreifend gelten. */
type SeasonViewMode = 'cards' | 'list';
const VIEW_MODE_STORAGE_KEY = 'bm:season-view-mode';

function loadSeasonViewMode(): SeasonViewMode {
  try {
    return localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'list' ? 'list' : 'cards';
  } catch {
    return 'cards';
  }
}

/** Turnier.de match comments (💬 icon on their Staffel "Spiele" overview) —
 * synced alongside the rest of the match data (periodic interval or the
 * manual "Aktualisieren" button), read here from storage rather than live. */
function MatchCommentsModal({
  matchId,
  subtitle,
  onClose,
}: {
  matchId: string;
  subtitle: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['season', 'match-comments', matchId],
    queryFn: () =>
      api<SeasonMatchCommentsResponse>(`/season/matches/${matchId}/comments`, { orgScoped: true }),
  });
  useLockBodyScroll();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[80vh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
            <MessageCircle size={18} />
            Kommentare
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>
        <p className="mb-4 text-xs text-slate-500">{subtitle}</p>

        {isLoading && <p className="text-sm text-slate-400">Lädt …</p>}
        {!isLoading && !data?.available && (
          <p className="text-sm text-slate-400">
            Keine Kommentare verfügbar — dieses Spiel ist nicht mit Turnier.de verknüpft.
          </p>
        )}
        {!isLoading && data?.available && data.comments.length === 0 && (
          <p className="text-sm text-slate-400">Noch keine Kommentare zu diesem Spiel.</p>
        )}
        {data?.available && data.comments.length > 0 && (
          <ul className="space-y-3">
            {data.comments.map((c, i) => (
              <li
                key={i}
                className="whitespace-pre-wrap rounded-md bg-slate-50 p-2.5 text-sm text-slate-700"
              >
                {c.text}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function formatSyncTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "Vergangen" heißt: der maßgebliche Termin (bei laufender Verlegung der
 * neue, sonst der ursprüngliche) liegt vor dem heutigen Tag — ein Spiel von
 * heute gilt noch nicht als vergangen. Ein Spiel ganz ohne Termin ("Termin
 * offen") wird nie ausgeblendet, da dessen Zeitpunkt ja gerade unbekannt
 * ist. `rescheduledDate` zählt nur bei `status === 'postponed'` — genau wie
 * beim "Verlegt auf den ..."-Badge weiter unten: läuft nach einer Verlegung
 * später ein echtes Ergebnis über Turnier.de ein, wird `status` auf
 * `played` gesetzt, aber `rescheduledDate` bewusst nicht mitgelöscht
 * (siehe packages/sync/src/match-sync.ts) — ein bereits gespieltes Spiel
 * darf sich dadurch nicht mehr an seinem alten Verlegungstermin statt am
 * echten `matchDate` orientieren. */
function isPastMatch(m: SeasonMatchResponse): boolean {
  const effectiveDate = m.status === 'postponed' ? (m.rescheduledDate ?? m.matchDate) : m.matchDate;
  if (!effectiveDate) return false;
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return effectiveDate < todayStr;
}

/** History of past "Von Turnier.de aktualisieren" runs for one season —
 * opened from the small summary line next to that button so a "did it
 * actually do anything" question has an answer beyond just trusting the
 * spinner stopped. */
function SyncHistoryModal({ seasonId, onClose }: { seasonId: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['season', 'sync-history', seasonId],
    queryFn: () => api<MatchSyncHistoryEntry[]>(`/season/seasons/${seasonId}/sync-history`, { orgScoped: true }),
  });
  useLockBodyScroll();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[80vh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
            <History size={18} />
            Turnier.de-Aktualisierungen
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>

        {isLoading && <p className="text-sm text-slate-400">Lädt …</p>}
        {!isLoading && data?.length === 0 && (
          <p className="text-sm text-slate-400">Noch keine Aktualisierung von Turnier.de für diese Saison.</p>
        )}
        {data && data.length > 0 && (
          <ul className="mt-2 space-y-2">
            {data.map((h) => (
              <li key={h.id} className="rounded-md bg-slate-50 p-2.5 text-sm">
                <div className="font-medium text-slate-700">{formatSyncTimestamp(h.createdAt)}</div>
                <div className="text-xs text-slate-500">
                  {h.teamsProcessed} Mannschaft(en) geprüft · {h.created} neu · {h.updated} aktualisiert
                  {' · '}
                  {h.actorEmail ? `manuell von ${h.actorEmail}` : 'automatisch'}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Photo/scan of the written match report. Choosing a photo only previews it
 * locally (object URL, nothing uploaded yet) — the actual upload happens on
 * "Speichern"; "Zurück" discards the pending choice and just closes. Clicking
 * the preview (existing or pending) opens it full-screen. */
function MatchReportModal({
  matchId,
  reportFileId,
  subtitle,
  canManage,
  onClose,
  onSaved,
}: {
  matchId: string;
  reportFileId: string | null;
  subtitle: string;
  canManage: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useLockBodyScroll();

  useEffect(() => {
    if (!pendingFile) return;
    const url = URL.createObjectURL(pendingFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  const save = useMutation({
    mutationFn: async () => {
      if (!pendingFile) return;
      const contentBase64 = await fileToBase64(pendingFile);
      await api(`/season/matches/${matchId}/report`, {
        method: 'PUT',
        orgScoped: true,
        body: { filename: pendingFile.name, mimeType: pendingFile.type, contentBase64 },
      });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Hochladen fehlgeschlagen.'),
  });

  const remove = useMutation({
    mutationFn: () => api(`/season/matches/${matchId}/report`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Löschen fehlgeschlagen.'),
  });

  // cache-bust so a just-replaced report shows immediately instead of the
  // browser's cached previous image at the same URL
  const src =
    previewUrl ?? (reportFileId ? `/api/season/matches/${matchId}/report?v=${reportFileId}` : null);

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[85vh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
            <Newspaper size={18} />
            Spielbericht
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>
        <p className="mb-4 text-xs text-slate-500">{subtitle}</p>

        {error && <Alert>{error}</Alert>}

        {src ? (
          <button
            type="button"
            onClick={() => setFullscreen(true)}
            className="block w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
          >
            <img src={src} alt="Spielbericht" className="max-h-72 w-full object-contain" />
          </button>
        ) : (
          <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-400">
            Noch kein Spielbericht hochgeladen.
          </p>
        )}
        {pendingFile && (
          <p className="mt-2 text-xs text-amber-600">Noch nicht gespeichert — erst mit „Speichern" übernehmen.</p>
        )}

        {canManage && (
          <div className="mt-4 space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => setPendingFile(e.target.files?.[0] ?? null)}
            />
            <Button
              type="button"
              variant="ghost"
              className="w-full gap-2"
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera size={16} />
              {src ? 'Anderes Foto aufnehmen' : 'Foto aufnehmen'}
            </Button>
            {reportFileId && !pendingFile && (
              <Button
                type="button"
                variant="danger"
                className="w-full gap-2"
                disabled={remove.isPending}
                onClick={() => {
                  if (confirm('Spielbericht wirklich löschen?')) remove.mutate();
                }}
              >
                <Trash2 size={16} />
                {remove.isPending ? 'Wird gelöscht…' : 'Spielbericht löschen'}
              </Button>
            )}
            <div className="flex gap-2">
              <Button type="button" variant="ghost" className="flex-1" onClick={onClose}>
                Zurück
              </Button>
              <Button
                type="button"
                className="flex-1"
                disabled={!pendingFile || save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? 'Speichert…' : 'Speichern'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>

    {fullscreen && src && (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
        onClick={() => setFullscreen(false)}
      >
        <img src={src} alt="Spielbericht" className="max-h-full max-w-full object-contain" />
        <button
          type="button"
          onClick={() => setFullscreen(false)}
          className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        >
          <X size={24} />
        </button>
      </div>
    )}
    </>
  );
}

function MatchCard({ m, onChanged }: { m: SeasonMatchResponse; onChanged: () => void }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [showRoster, setShowRoster] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showLineup, setShowLineup] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);

  const { data: overview } = useQuery({
    queryKey: ['voting', m.id],
    queryFn: () => api<VotingOverview>(`/season/matches/${m.id}/voting`, { orgScoped: true }),
  });
  const { theme } = useTheme();

  const respond = useMutation({
    mutationFn: (v: { memberId: string; status: 'accepted' | 'declined' | 'maybe' }) =>
      api(`/season/matches/${m.id}/respond`, {
        method: 'POST',
        orgScoped: true,
        body: { memberId: v.memberId, status: v.status },
      }),
    onSuccess: onChanged,
  });

  const reset = useMutation({
    mutationFn: (memberId: string) =>
      api(`/season/matches/${m.id}/voting/${memberId}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['voting', m.id] });
      onChanged();
    },
  });

  function respondWithCrossTeamWarning(memberId: string, teamIds: string[], status: 'accepted' | 'declined' | 'maybe') {
    if (
      teamIds.length > 0 &&
      m.teamId &&
      !teamIds.includes(m.teamId) &&
      !confirm(
        'Du gehörst nicht zur Mannschaft dieses Spiels — du meldest dich für eine andere Mannschaft an. Trotzdem fortfahren?',
      )
    ) {
      return;
    }
    respond.mutate({ memberId, status });
  }

  const time = m.matchTime ? m.matchTime.slice(0, 5) : null;
  // See TrainingPage.tsx's identical `showNames` for the reasoning — a lone
  // child still needs a name, a lone "you" doesn't.
  const showNames = m.respondableMembers.some((r) => !r.isOwn);
  const ownTeamName = m.homeOrAway === 'away' ? m.awayTeam : (m.homeTeam ?? m.awayTeam);
  const fullDateLabel = m.matchDate
    ? new Date(`${m.matchDate}T00:00:00`).toLocaleDateString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : 'Termin offen';
  const rescheduledLabel = m.rescheduledDate
    ? `${new Date(`${m.rescheduledDate}T00:00:00`).toLocaleDateString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })}${m.rescheduledTime ? ` · ${m.rescheduledTime.slice(0, 5)} Uhr` : ''}`
    : null;

  const participants = overview?.participants ?? [];
  const sortedParticipants = sortParticipantsForDisplay(participants);
  const rosterGroups: RosterGroup[] = [
    {
      key: 'accepted',
      label: 'Zugesagt',
      icon: <span className="text-green-600">✓</span>,
      countClass: 'text-green-600',
      rowClass: 'bg-green-50',
      items: participants.filter((p) => p.status === 'accepted').map((p) => ({ id: p.memberId, name: p.name })),
    },
    {
      key: 'maybe',
      label: 'Unentschlossen',
      icon: <span className="text-amber-600">?</span>,
      countClass: 'text-amber-600',
      rowClass: 'bg-amber-50',
      items: participants.filter((p) => p.status === 'maybe').map((p) => ({ id: p.memberId, name: p.name })),
    },
    {
      key: 'declined',
      label: 'Abgesagt',
      icon: <span className="text-red-600">✕</span>,
      countClass: 'text-red-600',
      rowClass: 'bg-red-50',
      items: participants.filter((p) => p.status === 'declined').map((p) => ({ id: p.memberId, name: p.name })),
    },
  ];

  // Ribbon über der ganzen Kartenbreite statt seitlichem Farbstreifen — der
  // Spielstatus ist so ohne Lesen erkennbar: Akzentfarbe = steht noch an,
  // grün = gespielt, rot = verlegt/abgesagt.
  const ribbonClass =
    m.status === 'postponed' || m.status === 'cancelled'
      ? 'bg-red-500'
      : m.status === 'played'
        ? 'bg-green-500'
        : 'bg-brand-accent';

  return (
    <div className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className={`h-1 w-full ${ribbonClass}`} />
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 sm:flex-nowrap sm:gap-3">
        <div className="flex w-full min-w-0 items-center gap-3 sm:w-auto sm:flex-1">
          <DateBadge date={m.matchDate} />
          {/* Gestrichelte statt durchgezogener Trennlinie — Ticket-Anmutung
              passend zur "Spielkarte" (siehe Farb-Ribbon oben). */}
          <div className="w-0 shrink-0 self-stretch border-l-2 border-dashed border-slate-300" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              {m.homeOrAway && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-500">
                  {m.homeOrAway === 'home' ? 'Heim' : 'Auswärts'}
                </span>
              )}
              {(m.league ?? m.division) && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-500">
                  {m.league ?? m.division}
                </span>
              )}
              {m.status === 'postponed' && (
                <span className="rounded-full bg-red-50 px-2 py-0.5 font-medium text-red-600">
                  {rescheduledLabel ? `Verlegt auf den ${rescheduledLabel}` : 'Verlegt'}
                </span>
              )}
              {m.status === 'cancelled' && (
                <span className="rounded-full bg-red-50 px-2 py-0.5 font-medium text-red-600">Abgesagt</span>
              )}
            </div>
            <div className="truncate text-sm font-semibold">
              {m.homeTeam ?? '—'} <span className="font-normal text-slate-400">vs.</span> {m.awayTeam ?? '—'}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
              <span>
                {time ?? 'Uhrzeit offen'}
                {m.venue ? ` · ${m.venue}` : ''}
                {m.status === 'played' && m.result && (
                  <span className="ml-1 font-medium text-slate-700">· {m.result}</span>
                )}
              </span>
              {m.counts.accepted > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">
                  <Check size={11} />
                  {m.counts.accepted}
                </span>
              )}
              {m.counts.declined > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600">
                  <X size={11} />
                  {m.counts.declined}
                </span>
              )}
              {can('season.view') && <LineupStatusBadge status={m.lineupStatus} />}
            </div>
            {participants.length > 0 && (
              // Nur ab Desktop-Breite — auf Mobilgeräten kosten die Namens-Kapseln
              // wertvollen vertikalen Platz, ohne den die Zusagen-/Absagen-Zahl
              // (oben in der Meta-Zeile) nicht schon zeigen würde; die volle
              // Liste bleibt über "Anmeldungen anzeigen" (Users-Icon) erreichbar.
              <div className="mt-1 hidden flex-wrap items-center gap-1 sm:flex">
                {sortedParticipants.map((p) => (
                  <span
                    key={p.memberId}
                    title={
                      p.sourceTeamId && p.sourceTeamId !== m.teamId
                        ? `${p.name} · ${STATUS_LABEL[p.status]} · andere Mannschaft`
                        : `${p.name} · ${STATUS_LABEL[p.status]}`
                    }
                    className={`rounded-full px-1.5 py-0.5 text-[11px] leading-4 ${rosterChipClass(CHIP_VARIANT[p.status], p.gender)} ${
                      p.sourceTeamId && m.teamId && p.sourceTeamId !== m.teamId ? 'ring-1 ring-amber-400' : ''
                    }`}
                  >
                    {p.firstName}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Eigene Fußleiste (statt Rückmeldung/Icons in derselben Zeile wie
          Datum+Begegnung): trennt die beiden Funktionsgruppen klar
          voneinander und wickelt auf schmalen Bildschirmen von selbst um
          (`flex-wrap`), ohne die vorherige eigens gebaute Mobil-/
          Desktop-Fallunterscheidung. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-slate-200 bg-slate-50 px-3 py-2">
        <div className="flex flex-col gap-1">
          {m.respondableMembers.map((r) => {
            const isAccepted = r.status === 'accepted';
            const isDeclined = r.status === 'declined';
            const isMaybe = r.status === 'maybe';
            const dim = (active: boolean) => (r.status !== 'pending' && !active ? { opacity: 0.3 } : {});
            return (
              <div key={r.memberId} className="flex items-center gap-1">
                {showNames && <span className="mr-1 max-w-20 truncate text-xs text-slate-500">{r.name}</span>}
                <Button
                  variant="ghost"
                  title="Anmelden"
                  aria-label="Anmelden"
                  className="leading-none transition hover:brightness-95 active:brightness-90"
                  style={{
                    ...responseButtonStyle('accepted', isAccepted, theme),
                    ...dim(isAccepted),
                  }}
                  onClick={() => respondWithCrossTeamWarning(r.memberId, r.teamIds, 'accepted')}
                >
                  <ThumbsUp size={14} />
                </Button>
                <Button
                  variant="ghost"
                  title="Abmelden"
                  aria-label="Abmelden"
                  className="leading-none transition hover:brightness-95 active:brightness-90"
                  style={{
                    ...responseButtonStyle('declined', isDeclined, theme),
                    ...dim(isDeclined),
                  }}
                  onClick={() => respondWithCrossTeamWarning(r.memberId, r.teamIds, 'declined')}
                >
                  <ThumbsDown size={14} />
                </Button>
                <Button
                  variant="ghost"
                  title="Vielleicht"
                  aria-label="Vielleicht"
                  className="leading-none transition hover:brightness-95 active:brightness-90"
                  style={{
                    ...responseButtonStyle('maybe', isMaybe, theme),
                    ...dim(isMaybe),
                  }}
                  onClick={() => respondWithCrossTeamWarning(r.memberId, r.teamIds, 'maybe')}
                >
                  <CircleHelp size={14} />
                </Button>
              </div>
            );
          })}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {can('season.view') && (
            <button
              type="button"
              title="Anmeldungen anzeigen"
              onClick={() => setShowRoster(true)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-200 hover:text-slate-600"
            >
              <Users size={15} />
            </button>
          )}
          {can('season.view') && (
            <button
              type="button"
              title="Mannschaftsaufstellung"
              onClick={() => setShowLineup(true)}
              className={
                m.lineupStatus === 'complete'
                  ? 'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-green-600 hover:bg-green-100'
                  : m.lineupStatus === 'incomplete'
                    ? 'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-amber-500 hover:bg-amber-100'
                    : 'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-200 hover:text-slate-600'
              }
            >
              <ListChecks size={15} />
            </button>
          )}
          {can('season.view') && (
            <button
              type="button"
              title={m.reportFileId ? 'Spielbericht ansehen' : 'Spielbericht hochladen'}
              onClick={() => setShowReport(true)}
              className={
                m.reportFileId
                  ? 'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sky-500 hover:bg-sky-100 hover:text-sky-600'
                  : 'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-200 hover:text-slate-600'
              }
            >
              <Newspaper size={15} />
            </button>
          )}
          {can('season.view') && (
            <button
              type="button"
              title="Verlegungsassistent"
              onClick={() => setShowReschedule(true)}
              className={
                m.status === 'postponed'
                  ? 'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-red-500 hover:bg-red-100'
                  : m.rescheduleHasOpenPoll
                    ? 'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-amber-500 hover:bg-amber-100'
                    : 'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-200 hover:text-slate-600'
              }
            >
              <ArrowLeftRight size={15} />
            </button>
          )}
          {can('season.view') && m.externalUrl && (
            <button
              type="button"
              title={m.hasComments ? 'Kommentare zu diesem Spiel verfügbar' : 'Kommentare zu diesem Spiel'}
              onClick={() => setShowComments(true)}
              className={
                m.hasComments
                  ? 'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-amber-500 hover:bg-amber-100 hover:text-amber-600'
                  : 'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-200 hover:text-slate-600'
              }
            >
              <MessageCircle size={15} />
            </button>
          )}
        </div>
      </div>
      {respond.isError && (
        <div className="px-3 pb-2">
          <Alert>{respond.error instanceof Error ? respond.error.message : 'Rückmeldung fehlgeschlagen.'}</Alert>
        </div>
      )}

      {showRoster && (
        <RosterModal
          title="Spieler-Anmeldungen"
          subtitle={`${fullDateLabel}${time ? ` · ${time}` : ''}${ownTeamName ? ` · ${ownTeamName}` : ''}`}
          groups={rosterGroups}
          onClose={() => setShowRoster(false)}
          onReset={can('matches.manage') ? (memberId) => reset.mutate(memberId) : undefined}
          resettingId={reset.isPending ? (reset.variables ?? null) : null}
          canAddPlayer={m.canManage}
          onSearchPlayers={(query) =>
            api<RespondablePlayerSearchResult[]>(
              `/season/matches/${m.id}/participants/search?q=${encodeURIComponent(query)}`,
              { orgScoped: true },
            ).then((rows) => rows.map((r) => ({ id: r.memberId, name: r.name })))
          }
          onAddPlayer={async (memberId) => {
            await respond.mutateAsync({ memberId, status: 'accepted' });
          }}
        />
      )}

      {showComments && (
        <MatchCommentsModal
          matchId={m.id}
          subtitle={`${fullDateLabel}${time ? ` · ${time}` : ''}${ownTeamName ? ` · ${ownTeamName}` : ''}`}
          onClose={() => setShowComments(false)}
        />
      )}

      {showReport && (
        <MatchReportModal
          matchId={m.id}
          reportFileId={m.reportFileId}
          subtitle={`${fullDateLabel}${time ? ` · ${time}` : ''}${ownTeamName ? ` · ${ownTeamName}` : ''}`}
          canManage={m.canManage}
          onClose={() => setShowReport(false)}
          onSaved={onChanged}
        />
      )}

      {showLineup && (
        <LineupModal
          match={m}
          onClose={() => {
            setShowLineup(false);
            onChanged();
          }}
        />
      )}

      {showReschedule && (
        <RescheduleModal
          match={m}
          onClose={() => {
            setShowReschedule(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/** Spieler-Kapseln für eine Tabellenzeile der Listenansicht — eigener,
 * schmaler Request je Zeile (`['voting', matchId]`, dieselbe Query wie
 * `MatchCard` verwendet), damit die Listenansicht ohne einen neuen,
 * eigens gebauten Batch-Endpunkt auskommt; dieselbe Query-Cache-Instanz wird
 * wiederverwendet, sobald für dasselbe Spiel schon eine Karte geladen war. */
function PlayerChipsCell({ matchId }: { matchId: string }) {
  const { data: overview } = useQuery({
    queryKey: ['voting', matchId],
    queryFn: () => api<VotingOverview>(`/season/matches/${matchId}/voting`, { orgScoped: true }),
  });
  const participants = sortParticipantsForDisplay(overview?.participants ?? []);
  if (participants.length === 0) return <span className="text-slate-300">—</span>;
  return (
    // `overflow-x-auto` statt `flex-wrap` — hält die Zeilenhöhe der Tabelle
    // niedrig (§ Nutzeranfrage "Spaltenhöhe sollte gering sein"), auch bei
    // vielen Spielern; bei Bedarf lässt sich diese eine Zelle horizontal
    // durchscrollen, statt die ganze Zeile mehrzeilig zu machen.
    <div className="flex flex-nowrap items-center gap-1 overflow-x-auto">
      {participants.map((p) => (
        <span
          key={p.memberId}
          title={`${p.name} · ${STATUS_LABEL[p.status]}`}
          className={`shrink-0 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[11px] leading-4 ${rosterChipClass(CHIP_VARIANT[p.status], p.gender)}`}
        >
          {p.firstName}
        </span>
      ))}
    </div>
  );
}

/** Kompakte Tabellenansicht als Alternative zu den Saisonspiel-Karten (§
 * Nutzeranfrage) — geringe Zeilenhöhe, damit auf einen Blick möglichst viele
 * Spiele sichtbar sind; Aktionen (Anmelden, Aufstellung, Spielbericht, …)
 * bleiben ausschließlich der Kartenansicht vorbehalten, diese Ansicht ist
 * ein reiner, schneller Überblick. Respektiert dieselben Filter
 * (Saison/Mannschaft/vergangene Spiele) wie die Kartenansicht — beide lesen
 * aus derselben `visibleMatches`-Liste. */
function MatchListView({ matches }: { matches: SeasonMatchResponse[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <th className="px-3 py-1.5">Datum</th>
            <th className="px-3 py-1.5">Zeit</th>
            <th className="px-3 py-1.5">Heim.</th>
            <th className="px-3 py-1.5">Ausw.</th>
            <th className="px-3 py-1.5">Spieler</th>
            <th className="px-3 py-1.5">Turnier.de</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {matches.map((m) => {
            const dateLabel = m.matchDate
              ? new Date(`${m.matchDate}T00:00:00`).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : 'Termin offen';
            const timeLabel = m.matchTime ? m.matchTime.slice(0, 5) : '—';
            return (
              <tr key={m.id} className="hover:bg-slate-50">
                <td className="whitespace-nowrap px-3 py-1 text-slate-600">{dateLabel}</td>
                <td className="whitespace-nowrap px-3 py-1 text-slate-600">{timeLabel}</td>
                <td className="max-w-[180px] truncate px-3 py-1 font-medium text-slate-700" title={m.homeTeam ?? undefined}>
                  {m.homeTeam ?? '—'}
                </td>
                <td className="max-w-[180px] truncate px-3 py-1 font-medium text-slate-700" title={m.awayTeam ?? undefined}>
                  {m.awayTeam ?? '—'}
                </td>
                <td className="max-w-[360px] px-3 py-1">
                  <PlayerChipsCell matchId={m.id} />
                </td>
                <td className="whitespace-nowrap px-3 py-1">
                  {m.externalUrl ? (
                    <a
                      href={m.externalUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-brand-accent hover:underline"
                    >
                      <ExternalLink size={12} /> Turnier.de
                    </a>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function SeasonPage() {
  const qc = useQueryClient();
  const { can, me, refresh } = useAuth();
  // Pro Benutzerkonto gespeichert (users.hide_past_season_matches), nicht nur
  // im Browser — bleibt beim nächsten Besuch, auch auf einem anderen Gerät,
  // aktiv. Gleiches Muster wie die "Startseite"-Präferenz (ProfileMenu.tsx).
  const hidePastMatches = me?.user.hidePastSeasonMatches ?? false;
  const setHidePastMatches = useMutation({
    mutationFn: (value: boolean) =>
      api('/auth/me/hide-past-season-matches', { method: 'PATCH', body: { hidePastSeasonMatches: value } }),
    onSuccess: () => void refresh(),
  });
  const [viewMode, setViewModeState] = useState<SeasonViewMode>(loadSeasonViewMode);
  function setViewMode(mode: SeasonViewMode) {
    setViewModeState(mode);
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // Browser-Bequemlichkeit — ein blockierter/voller localStorage darf
      // die Ansicht selbst nicht verhindern, nur die Merkfähigkeit entfällt.
    }
  }
  const [seasonId, setSeasonId] = useState<string>('');
  // "Nur meine Mannschaft"/Team-Filter: pro Benutzer UND pro Verein
  // gespeichert (organization_members.season_match_filter_*, § Nutzeranfrage
  // "soll die Filterung ... gespeichert werden"), nicht nur im Browser —
  // bleibt beim nächsten Besuch der Seite erhalten, auch auf einem anderen
  // Gerät. Gleiches Prinzip wie `hidePastMatches` oben, nur pro Verein statt
  // global, da eine Team-ID nur innerhalb eines Vereins Sinn ergibt.
  //
  // Persistenz schreibt bei JEDER Änderung sofort durch (kein Debounce) —
  // exakt wie `setHidePastMatches` oben. Ein früherer Entwurf verzögerte den
  // Team-Filter-Schreibvorgang um 600ms, um mehrere schnell aufeinanderfolgende
  // Dropdown-Klicks zu einem Request zu bündeln. Per Browser-Test (echter
  // Server, echte Navigation) bestätigt: Ein Klick auf einen Link innerhalb
  // der App übersteht einen ausstehenden Timer zwar (JS-Kontext bleibt
  // erhalten), aber jede ECHTE Navigation — Browser-Zurück, Tab schließen,
  // manuelles Neuladen — reißt den JS-Kontext samt Timer sofort ab, ganz
  // unabhängig davon, ob der Timer beim Unmount explizit gecancelt wird.
  // Genau das ist der naheliegendste reale Ablauf ("Checkbox anklicken und
  // direkt weiter"), also lohnt sich die Bündelung nicht — sie kann nur
  // Datenverlust verursachen, nie zuverlässig davor schützen. Ein Request je
  // Klick ist für eine überschaubare Anzahl Mannschaften ohnehin
  // vernachlässigbar (gleiches Prinzip wie die einzelnen `respond.mutate()`-
  // Aufrufe je Zu-/Absage-Klick andernorts auf dieser Seite).
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [mine, setMine] = useState(true);

  // Ein Vereinswechsel lädt `seasons` neu, lässt `seasonId` aber unverändert
  // stehen — das referenziert dann eine Saison aus dem VORHERIGEN Verein, die
  // es im neuen gar nicht gibt. Die Spiele-Abfrage lief bis zu diesem Fix mit
  // dieser fremden `seasonId` leer (RLS filtert sie weg), sichtbar behoben
  // nur durch manuelles Neuladen der Seite. Zurücksetzen bei jedem Wechsel
  // lässt den Auto-Auswahl-Effekt unten von selbst wieder die richtige
  // Saison für den neuen Verein wählen. `mine`/`teamIds` werden im selben
  // Zug aus dem (für den neuen Verein bereits aktuellen) `me.user` geladen —
  // dasselbe `me`-Objekt, aus dem auch `hidePastMatches` oben liest, wird
  // nach jedem Vereinswechsel ohnehin neu geladen (`AuthProvider.selectOrg`).
  // Rein lesend (kein Schreibvorgang hier) — die Persistenz läuft
  // ausschließlich über die onChange-Handler unten, nie automatisch als
  // Reaktion auf diesen Effekt.
  useEffect(() => {
    setSeasonId('');
    setMine(me?.user.seasonMatchFilterOnlyMyTeam ?? true);
    setTeamIds(me?.user.seasonMatchFilterTeamIds ?? []);
  }, [me?.activeOrganizationId]);

  const setSeasonMatchFilter = useMutation({
    mutationFn: (body: { onlyMyTeam: boolean; teamIds: string[] }) =>
      api('/auth/me/season-match-filter', { method: 'PATCH', orgScoped: true, body }),
    // Ohne dies blieb der `['me']`-Query-Cache (staleTime 30s) auf dem alten
    // Stand stehen — verließ man die Seite (Client-seitiger Routenwechsel,
    // kein Reload) und kam zurück, hydrierte der Effekt oben aus genau diesem
    // veralteten Cache und verwarf die gerade erst gespeicherte Änderung
    // sichtbar wieder (§ Nutzerfeedback: "4. Mannschaft fehlt nach
    // Seitenwechsel, erst ein harter Reload bringt sie zurück" — exakt dieses
    // Symptom). `refresh()` (voller `/auth/me`-Neuabruf, wie bei
    // `hidePastMatches`) wäre ebenfalls korrekt, aber unnötig langsam, da wir
    // die gerade bestätigt gespeicherten Werte schon kennen — direktes
    // Schreiben in den Cache reicht.
    onSuccess: (_data, variables) => {
      qc.setQueryData<MeResponse | null>(['me'], (old) =>
        old
          ? {
              ...old,
              user: {
                ...old.user,
                seasonMatchFilterOnlyMyTeam: variables.onlyMyTeam,
                seasonMatchFilterTeamIds: variables.teamIds,
              },
            }
          : old,
      );
    },
  });

  function persistSeasonMatchFilter(onlyMyTeam: boolean, nextTeamIds: string[]) {
    setSeasonMatchFilter.mutate({ onlyMyTeam, teamIds: nextTeamIds });
  }

  const { data: seasons } = useQuery({
    queryKey: ['seasons'],
    queryFn: () => api<SeasonResponse[]>('/season/seasons', { orgScoped: true }),
  });

  useEffect(() => {
    if (!seasons || seasons.length === 0 || seasonId) return;
    const active = seasons.find((s) => s.status === 'active') ?? seasons[0];
    if (active) setSeasonId(active.id);
  }, [seasons, seasonId]);

  const season = seasons?.find((s) => s.id === seasonId);

  const { data: matches } = useQuery({
    queryKey: ['season', 'matches', seasonId, teamIds, mine],
    queryFn: () =>
      api<{ data: SeasonMatchResponse[] }>(
        `/season/matches?pageSize=200${seasonId ? `&seasonId=${seasonId}` : ''}${teamIds.length ? `&teamIds=${teamIds.join(',')}` : mine ? '&mine=true' : ''}`,
        { orgScoped: true },
      ),
    enabled: !!seasonId,
  });

  const invalidate = () => qc.invalidateQueries();

  // § Nutzeranfrage „Sync-Button soll weg — die Spiele werden automatisch
  // aktualisiert": der manuelle Anstoß-Button für den Turnier.de-Sync ist
  // entfallen (bleibt weiterhin über den dedizierten Verwaltungspunkt
  // „Turnier.de Sync", `TurnierDePage.tsx`, verfügbar, falls doch einmal ein
  // sofortiger Sync nötig ist). Hier bleibt nur die rein informative
  // „Zuletzt synchronisiert"-Zeile samt Verlauf — kein eigener Zustand mehr
  // fürs Polling/die Erfolgs-/Fehlermeldung eines hier ausgelösten Syncs.
  const canSyncFromTurnierDe = can('season.edit') && can('teams.sync');
  const { data: turnierDeStatus } = useQuery({
    queryKey: ['turnier-de', 'status'],
    queryFn: () => api<TurnierDeStatusDto>('/org/turnier-de/status', { orgScoped: true }),
    enabled: canSyncFromTurnierDe,
  });
  const [showSyncHistory, setShowSyncHistory] = useState(false);
  const { data: syncHistory } = useQuery({
    queryKey: ['season', 'sync-history', seasonId],
    queryFn: () => api<MatchSyncHistoryEntry[]>(`/season/seasons/${seasonId}/sync-history`, { orgScoped: true }),
    enabled: canSyncFromTurnierDe && !!seasonId && !!turnierDeStatus?.connected,
  });
  const lastSync = syncHistory?.[0];

  const visibleMatches = matches?.data.filter((m) => !hidePastMatches || !isPastMatch(m));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="hidden text-xl font-semibold md:block">Saisonspiele</h1>
          <p className="text-sm text-slate-500">
            Alle Saisonspiele unserer Mannschaften · Zusagen oder Absagen · ein Spiel pro Tag
            {season ? ` · ${season.name}` : ''}
          </p>
        </div>
        {/* `flex-wrap` (§ Nutzerfeedback "Inhalt wird außerhalb des
            Content-Bereichs dargestellt") — ohne das lief diese Gruppe auf
            schmalen Mobil-Viewports über die Bildschirmbreite hinaus, was
            die ganze Seite horizontal überlaufen ließ. */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Karten-/Listenansicht (§ Nutzeranfrage) — nur eine Darstellung
              der bereits gefilterten Spiele, kein zusätzlicher Filter. */}
          <div className="flex items-center rounded-md border border-slate-300 p-0.5">
            <button
              type="button"
              title="Kartenansicht"
              aria-pressed={viewMode === 'cards'}
              onClick={() => setViewMode('cards')}
              className={`flex h-7 w-7 items-center justify-center rounded ${
                viewMode === 'cards' ? 'bg-brand-accent text-white' : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              <LayoutGrid size={14} />
            </button>
            <button
              type="button"
              title="Listenansicht"
              aria-pressed={viewMode === 'list'}
              onClick={() => setViewMode('list')}
              className={`flex h-7 w-7 items-center justify-center rounded ${
                viewMode === 'list' ? 'bg-brand-accent text-white' : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              <List size={14} />
            </button>
          </div>
          {/* § Nutzeranfrage „Filterung … modern und professionell gestalten"
           * — Filter-Chips statt nativer Checkboxen: plattformabhängiges
           * Browser-Standard-Rendering (vgl. Screenshot, iOS' groß/blau)
           * ersetzt durch denselben kompakten, umrandeten Button-Stil wie
           * die Gruppen-/Wochentag-Filter-Dropdowns anderswo in der App —
           * gefüllt in Akzentfarbe plus Haken, wenn aktiv, sonst nur
           * umrandet. Gleiche Umschalt-Logik wie zuvor, nur als Klick statt
           * Checkbox-Change. */}
          {season && season.teams.length > 0 && (
            <button
              type="button"
              aria-pressed={mine}
              onClick={() => {
                const checked = !mine;
                const nextTeamIds = checked ? [] : teamIds;
                setMine(checked);
                if (checked) setTeamIds([]);
                persistSeasonMatchFilter(checked, nextTeamIds);
              }}
              className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                mine
                  ? 'border-brand-accent bg-brand-accent/10 text-brand-accent'
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {mine && <Check size={14} className="shrink-0" />}
              Nur meine Mannschaft
            </button>
          )}
          <button
            type="button"
            aria-pressed={hidePastMatches}
            disabled={setHidePastMatches.isPending}
            onClick={() => setHidePastMatches.mutate(!hidePastMatches)}
            className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
              hidePastMatches
                ? 'border-brand-accent bg-brand-accent/10 text-brand-accent'
                : 'border-slate-300 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {hidePastMatches && <Check size={14} className="shrink-0" />}
            Vergangene Spiele ausblenden
          </button>
          {season && season.teams.length > 0 && !mine && (
            <TeamFilterDropdown
              teams={season.teams}
              selected={teamIds}
              onChange={(ids) => {
                setTeamIds(ids);
                persistSeasonMatchFilter(mine, ids);
              }}
            />
          )}
          {seasons && seasons.length > 1 && (
            <select
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              value={seasonId}
              onChange={(e) => {
                setSeasonId(e.target.value);
                setTeamIds([]);
              }}
            >
              {seasons.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {canSyncFromTurnierDe && turnierDeStatus?.connected && (
        <div className="-mt-2 flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
          {lastSync ? (
            <span>
              Zuletzt von Turnier.de aktualisiert: {formatSyncTimestamp(lastSync.createdAt)} · {lastSync.created} neu
              · {lastSync.updated} aktualisiert · {lastSync.actorEmail ? `manuell von ${lastSync.actorEmail}` : 'automatisch'}
            </span>
          ) : (
            <span>Noch nie von Turnier.de aktualisiert.</span>
          )}
          <button
            type="button"
            onClick={() => setShowSyncHistory(true)}
            className="inline-flex items-center gap-1 text-brand-accent hover:underline"
          >
            <History size={12} /> Verlauf
          </button>
        </div>
      )}

      {showSyncHistory && seasonId && (
        <SyncHistoryModal seasonId={seasonId} onClose={() => setShowSyncHistory(false)} />
      )}

      <div className={viewMode === 'cards' ? 'grid gap-3' : 'space-y-3'}>
        {viewMode === 'cards' &&
          visibleMatches?.map((m) => <MatchCard key={m.id} m={m} onChanged={invalidate} />)}
        {viewMode === 'list' && visibleMatches && visibleMatches.length > 0 && (
          <MatchListView matches={visibleMatches} />
        )}
        {visibleMatches?.length === 0 && (matches?.data.length ?? 0) > 0 && hidePastMatches && (
          <p className="text-sm text-slate-500">
            Alle Saisonspiele dieser Auswahl sind bereits vergangen.{' '}
            <button
              type="button"
              className="text-brand-accent hover:underline"
              onClick={() => setHidePastMatches.mutate(false)}
            >
              Vergangene Spiele wieder anzeigen
            </button>
          </p>
        )}
        {matches?.data.length === 0 && mine && (
          <p className="text-sm text-slate-500">
            Keine Saisonspiele deiner Mannschaft für diese Auswahl.{' '}
            <button type="button" className="text-brand-accent hover:underline" onClick={() => setMine(false)}>
              Alle Mannschaften anzeigen
            </button>
          </p>
        )}
        {matches?.data.length === 0 && !mine && (
          <p className="text-sm text-slate-500">Keine Saisonspiele für diese Auswahl.</p>
        )}
        {!seasonId && (
          <p className="text-sm text-slate-500">
            Zuerst eine Saison unter{' '}
            {can('season.edit') ? (
              <Link to="/organization/seasons" className="text-brand-accent hover:underline">
                Verwaltung › Verein › Saisons
              </Link>
            ) : (
              'Verwaltung › Verein › Saisons'
            )}{' '}
            anlegen.
          </p>
        )}
      </div>
    </div>
  );
}
